import { ServiceBooking } from '@turizi/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Affix } from '@mantine/core';
import { IconCheck, IconExternalLink, IconX } from '@tabler/icons-react';
import { useCreateServiceOrder, useAddBookingToOrder } from '@/hooks/api-hooks';
import { EDIT_SERVICE_ORDER_PATH } from '@/lib/navigation';
import { Link } from 'react-router-dom';


interface Props {
    selectedBookings: ServiceBooking[];
    onClearSelection: () => void;
}


export default function ServiceOrderWidget({
    selectedBookings,
    onClearSelection,
}: Props) {
    const { t } = useTranslation();
    const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);

    const createServiceOrderToBooking = useAddBookingToOrder();
    const createServiceOrder = useCreateServiceOrder();

    const totalPax = selectedBookings.reduce((sum, b) => sum + (b.adults || 0) + (b.children || 0) + (b.seniors || 0), 0);

    const handleCreateOrderFromSelection = () => {
        if (selectedBookings.length === 0) return;

        createServiceOrder.mutate({
        }, {
            onSuccess: (data) => {
                const newOrderId = data.data.id;
                // Add all selected bookings to the new order
                selectedBookings.forEach(booking => {
                    createServiceOrderToBooking.mutate({
                        serviceOrderId: newOrderId,
                        serviceBookingId: booking.id
                    });
                });
                setCreatedOrderId(newOrderId);
                onClearSelection();
            }
        });
    };

    if (selectedBookings.length === 0 && !createdOrderId) {
        return null;
    }

    return (
        <Affix
            position={{ bottom: 24, right: 24 }}
            zIndex={100}
            className="w-[calc(100vw-32px)] md:w-auto"
        >
            <style>{`
                @keyframes widget-slide-up {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.98);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .widget-container {
                    animation: widget-slide-up 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>

            <div className="widget-container flex flex-col sm:flex-row items-center justify-between gap-4 p-3 px-5 rounded-2xl md:rounded-full bg-zinc-950/90 text-white backdrop-blur border border-zinc-800 shadow-2xl min-w-[300px] md:min-w-[420px]">
                {createdOrderId ? (
                    <>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-sm font-medium text-zinc-100">
                                {t('service_order')}: <span className="font-bold text-emerald-400">#{createdOrderId}</span> {t('created', 'created!')}
                            </span>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                            <Button
                                size='xs'
                                variant="subtle"
                                color="white"
                                className="text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-full"
                                onClick={() => {
                                    setCreatedOrderId(null);
                                }}
                            >
                                {t('new_order')}
                            </Button>
                            <Link
                                to={EDIT_SERVICE_ORDER_PATH.replace(':id', createdOrderId.toString())}>
                                <Button
                                    rightSection={<IconExternalLink size={14} />}
                                    color="blue"
                                    size='xs'
                                    radius="xl"
                                    className="shadow-lg hover:shadow-blue-500/20"
                                >
                                    {t('view_order')}
                                </Button>
                            </Link>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex items-center gap-4 flex-1 w-full justify-between sm:justify-start">
                            <div className="flex items-center gap-2">
                                <div className="bg-blue-600 text-white font-extrabold w-6 h-6 rounded-full flex items-center justify-center text-xs">
                                    {selectedBookings.length}
                                </div>
                                <span className="text-[11px] text-zinc-400 font-semibold uppercase tracking-wider">
                                    {t('bookings_selected')}
                                </span>
                            </div>
                            <span className="text-zinc-850 hidden sm:inline">|</span>
                            <div className="flex items-center gap-1.5">
                                <span className="font-bold text-md text-zinc-100">
                                    {totalPax}
                                </span>
                                <span className="text-[11px] text-zinc-400 font-semibold uppercase tracking-wider">
                                    {t('pax')}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                            <Button
                                size='xs'
                                variant="subtle"
                                color="white"
                                leftSection={<IconX size={14} />}
                                className="text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-full"
                                onClick={onClearSelection}
                            >
                                {t('clear')}
                            </Button>
                            <Button
                                size='xs'
                                leftSection={<IconCheck size={14} />}
                                color="blue"
                                radius="xl"
                                className="shadow-lg hover:shadow-blue-500/20 active:scale-95 transition-all"
                                onClick={handleCreateOrderFromSelection}
                                loading={createServiceOrder.isPending}
                            >
                                {t('create_service_order')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Affix>
    );
}
